<template>
    <div>
        <!-- Ticket Description (auto generated and manual) -->
        <div v-if="show" class="absolute bottom-0 w-full">
            <div class="w-full">
                <div class="font-medium text-md">{{ form.ticket_details.auto_generated_description ? "Auto Generated Description" : "Custom Description" }}</div>

                <text-area
                    class="overscroll-none overflow-y-auto scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100 mb-1"
                    v-model="form.ticket_details.description"
                    @input="form.ticket_details.auto_generated_description = false; updateMutableMaxTextLength()"
                    placeholder="The ticket's description will be auto generated when you create issues but can also be manually changed"
                    rows="15"
                    :with-label="false"
                    input-label=""
                    :resizable="false"
                    :maxLength="mutableMaxTextLength">
                </text-area>

                <span class="text-gray-400">{{ charactersLeft }}</span>

                <div class="flex flex-col mt-3 sm:flex-row sm:justify-end gap-x-4 space-y-2 sm:space-y-0 mt-2">
                    <button class="text-red-600 hover:text-brand flex items-center text-sm" @click="setTicketToAutoDescription(false)">
                        <icon-trash class="w-4 h-4 mr-1"></icon-trash>
                        Revert To Auto Description
                    </button>

                    <button class="text-green-500 hover:text-green-700 flex items-center text-sm" @click="setTicketManualDescription()">
                        <icon-check class="w-4 h-4 mr-1"></icon-check>
                        Confirm Custom Description
                    </button>
                </div>
            </div>
        </div>

        <div class="flex justify-end items-center absolute bottom-0 left-0 right-0"
             v-if="!show && Object.keys(issueToEdit).length === 0 && !isShowingPhoto">

            <!-- Enable custom description button -->
            <div class="flex items-center space-x-2">
                <button class="text-indigo-600 hover:text-indigo-800 flex items-center" @click="showCustomDescription=true, $emit('open')">
                    Custom Ticket Description
                </button>
                <button @click.prevent="showTicketDescriptionExplanation = true" class="text-indigo-600 hover:text-indigo-800 transition duration-300"
                        title="What is a custom description?">
                    <icon-help class="w-4 h-4"></icon-help>
                </button>
            </div>
        </div>

        <simple-modal :show="showTicketDescriptionExplanation" title="Ticket Description Explanation" @close="showTicketDescriptionExplanation = false"
                      max-width="sm:max-w-lg">
            <ol class="flex flex-col gap-y-2">
                <li class="flex flex-col">
                    <span
                        class="mb-2">Ticket descriptions are usually automatically managed by RedRabbit. With the creation and deletion of ticket issues, we will update your
                        ticket's description with a list of the issues, usually similar to something like this:</span>

                    <span class="whitespace-pre">
                        Ticket opened with 2 items

                        1. [Bathroom] Toilet
                        2. [Bathroom] Basin
                    </span>
                    <span class="mb-2">If you want to edit the description for any reason, we will no longer update the description automatically when you have made
                        changes to
                        the ticket's issues. While creating a ticket,
                        you can either keep the ticket's description on automatic mode, or edit it to be your own.</span>

                    <span> Use the <span class="text-green-500">Confirm Custom Description</span> button to save your personal changes.</span>
                    <span> Use the <span class="text-red-600">Revert To Auto Description</span> to override your changes and have RedRabbit automatically update your
                        description with your ticket's issues.</span>
                </li>
            </ol>
        </simple-modal>
    </div>
</template>

<script>
export default {
    name: "TicketAutoDescription",

    props: {
        show: {
            required: true,
            type: Boolean,
        },
        form: {
            required: true,
            type: Object,
        },
        issueToEdit: {
            required: false,
            type: Object,
            default: () => {
                return {};
            }
        },
        isShowingPhoto: {
            required: false,
            type: Boolean,
            default: false,
        },
        inspection: {
            required: false,
            type: Object,
            default: () => {
                return null;
            },
        },
        gotFocus: {
            required: false,
            type: Boolean,
            default: false,
            description: "Used when previous then next buttons was clicked to trigger on focus events"
        }
    },

    data () {
        return {
            showTicketDescriptionExplanation: false,
            showCustomDescription: false,
            fixedMaxTextLength: 2000,
            mutableMaxTextLength: 2000
        };
    },

    watch: {
        gotFocus (gotFocus) {
            if (gotFocus && this.showCustomDescription) {
                this.$emit("open");
            }
        }
    },

    computed: {
        charactersLeft () {
            let textLength = this.textLength(this.form.ticket_details.description);
            return (this.fixedMaxTextLength - textLength) + " / " + this.fixedMaxTextLength + " characters remaining";
        },
    },

    methods: {
        textLength (text) {
            return text.length + this.specialCharsCount(this.form.ticket_details.description);
        },

        specialCharsCount (text) {
            const surrogatePairs = text.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g) || [];
            const lineEndings = text.match(/[\n\r]/g) || [];
            const nonASCIIChars = text.match(/[^\u0000-\u007F]/g) || [];

            return surrogatePairs.length + lineEndings.length + nonASCIIChars.length;
        },

        updateMutableMaxTextLength () {
            this.mutableMaxTextLength = this.fixedMaxTextLength - this.specialCharsCount(this.form.ticket_details.description);
            if (this.textLength(this.form.ticket_details.description) > this.mutableMaxTextLength) {
                this.form.ticket_details.description = this.form.ticket_details.description.substring(0, this.mutableMaxTextLength);
            }
        },

        autoGenerateTicketDescription () {
            this.$emit("close");
            this.form.ticket_details.auto_generated_description = true;

            let nrIssues = this.form.issues ? this.form.issues.length : 0;
            let description = "";
            let descriptionLength = 0;

            // Header of ticket description
            if (this.inspection) {
                description = "Ticket opened from " + this.inspection.type + " Inspection on " + this.inspection.date +
                    " with " + (nrIssues > 0 ? nrIssues : "no") + " items \n\n";

                descriptionLength = description.length;
            } else {
                description = "Ticket opened with " + (nrIssues > 0 ? nrIssues : "no") + " items \n\n";

                descriptionLength = description.length;
            }

            // issues and ending
            if (nrIssues) {
                this.form.issues.every((issue, index) => {
                    let tempDescription = "";
                    tempDescription += (index + 1) + ". ";
                    tempDescription += issue.location ? "[" + issue.location + "] " : " ";
                    tempDescription += issue.item;

                    issue.description ? tempDescription += " - " + issue.description : "";
                    issue.recommendation ? tempDescription += " (" + issue.recommendation + ")" : "";
                    tempDescription += "\n";

                    // 50 is the generous length of "... and x other items" to allow the description to include the sentence.
                    if (tempDescription.length + descriptionLength + 50 > 1950) {
                        description += "\n...and " + (nrIssues - index) + " other items.";
                        return false;
                    }

                    description += _.upperFirst(tempDescription);
                    descriptionLength = description.length;
                    // return true to iterate and false to exit a .every().
                    return true;
                });
            }

            return description;
        },

        setTicketManualDescription () {
            // Description is already v-modeled so we dont set it
            this.form.ticket_details.auto_generated_description = false;
            this.showCustomDescription = false;
            this.$emit("close");
        },

        setTicketToAutoDescription (returning = true) {
            this.showCustomDescription = false;
            if (this.form && this.form.ticket_details) {
                this.form.ticket_details.auto_generated_description = true;
                if (returning) {
                    return this.autoGenerateTicketDescription();
                } else {
                    this.form.ticket_details.description = this.autoGenerateTicketDescription();
                }
            }
        },
    },
};
</script>
